import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { SeoArticle } from "@/components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [all, setAll] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    Promise.all([
      axios.get(`${API}/articles/${slug}`),
      axios.get(`${API}/articles`),
    ])
      .then(([a, list]) => {
        setArticle(a.data);
        setAll(list.data);
        setState("ok");
      })
      .catch(() => setState("missing"));
  }, [slug]);

  if (state === "missing") return <Navigate to="/journal" replace />;
  if (state === "loading" || !article) {
    return (
      <div
        className="min-h-screen bg-[#f7f5f0] flex items-center justify-center"
        data-testid="journal-article-page"
      >
        <span className="font-serif-ed italic text-2xl text-[#1a1918]/60">
          Chargement…
        </span>
      </div>
    );
  }

  const idx = all.findIndex((a) => a.slug === slug);
  const next = all[(idx + 1) % all.length] || article;

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1a1918]" data-testid="journal-article-page">
      <SeoArticle article={article} />
      <header className="border-b border-[#1a1918]/15">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-serif-ed text-xl md:text-2xl">
            ProSecours
          </Link>
          <Link to="/journal" className="overline text-[#1a1918]/70 hover:text-[#1a1918]">
            ← Journal
          </Link>
        </div>
      </header>

      <article className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-12 gap-4"
        >
          <div className="col-span-12 md:col-span-8 md:col-start-3">
            <div className="flex items-center gap-4 overline text-[#1a1918]/60">
              <span className="text-[#ce1e20]">{article.tag}</span>
              <span>·</span>
              <span>{article.date}</span>
            </div>
            <h1 className="mt-6 font-serif-ed text-5xl md:text-7xl leading-[1.02] tracking-tight">
              {article.title}
            </h1>
            <p className="mt-8 font-serif-ed italic text-xl md:text-2xl text-[#1a1918]/80 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-4 mt-16 md:mt-24">
          <div className="col-span-12 md:col-span-8 md:col-start-3 space-y-10">
            {/* En bref — teaser volontairement court */}
            {article.body[0] && article.body[0].p && article.body[0].p[0] && (
              <motion.section
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8 }}
                className="border-t border-[#1a1918]/20 pt-8"
              >
                <div className="overline text-[#1a1918]/60 mb-4">En bref</div>
                <p className="text-lg md:text-xl leading-relaxed text-[#1a1918]/85 font-serif-ed italic">
                  {article.body[0].p[0]}
                </p>
              </motion.section>
            )}

            {/* Message calibré — la procédure est complexe, un accompagnement est nécessaire */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="border-t border-[#1a1918]/20 pt-8"
            >
              <div className="chapter-num text-2xl text-[#1a1918]/60">01</div>
              <h2 className="mt-2 font-serif-ed text-3xl md:text-4xl italic leading-tight">
                Une procédure qui ne s'improvise pas.
              </h2>
              <div className="mt-6 space-y-4 text-base md:text-lg leading-relaxed text-[#1a1918]/85">
                <p>
                  Chaque dossier comporte des délais, des documents exigés et
                  un greffe compétent. Vous choisissez la procédure ; nous
                  préparons les actes, réunissons les pièces et organisons le
                  dépôt.
                </p>
                <p>
                  Nous pouvons également demander la date auprès du tribunal
                  lorsque la procédure l'exige, puis suivre le dossier jusqu'à
                  confirmation par le greffe compétent.
                </p>
              </div>
            </motion.section>
          </div>
        </div>

        {/* Grand bloc CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-12 gap-4 mt-20 md:mt-28"
        >
          <div className="col-span-12 md:col-span-8 md:col-start-3">
            <div className="bg-[#1a1918] text-[#f7f5f0] p-8 md:p-14">
              <div className="overline text-[#f7f5f0]/60">
                Passer à l'action
              </div>
              <h3 className="mt-6 font-serif-ed text-3xl md:text-5xl leading-[1.05]">
                Vous choisissez la procédure.{" "}
                <span className="italic text-[#efece5]/95">
                  Nous préparons le dossier
                </span>
                .
              </h3>
              <p className="mt-6 text-base md:text-lg text-[#f7f5f0]/80 max-w-xl leading-relaxed">
                Rédaction des actes, rassemblement des pièces, demande de date
                auprès du tribunal si nécessaire et dépôt auprès du greffe
                compétent. Premier échange gratuit, sans engagement.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/#audit"
                  data-testid="article-cta-demarche"
                  className="inline-flex items-center gap-3 px-6 py-4 bg-[#f7f5f0] text-[#1a1918] overline hover:bg-[#ce1e20] hover:text-[#f7f5f0] transition-colors"
                >
                  Décrire ma démarche
                  <span>→</span>
                </Link>
                <a
                  href="tel:+33744883381"
                  data-testid="article-cta-phone"
                  className="ed-link overline text-[#f7f5f0]"
                >
                  ou appelez le 07 44 88 33 81
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Next + CTA */}
        <div className="grid grid-cols-12 gap-4 mt-24 md:mt-32">
          <div className="col-span-12 md:col-span-8 md:col-start-3 border-t border-[#1a1918]/25 pt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <div className="overline text-[#1a1918]/60">Article suivant</div>
              <Link
                to={`/journal/${next.slug}`}
                data-testid="article-next"
                className="ed-link mt-3 inline-block font-serif-ed italic text-3xl md:text-4xl text-[#1a1918]"
              >
                {next.title} →
              </Link>
            </div>
            <Link
              to="/#audit"
              data-testid="article-cta-audit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1918] text-[#f7f5f0] overline hover:bg-[#ce1e20] transition-colors"
            >
              Prendre rendez-vous →
            </Link>
          </div>
        </div>
      </article>

      <footer className="border-t border-[#1a1918]/15 py-8">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 overline text-[#1a1918]/50">
          © MMXXV ProSecours
        </div>
      </footer>
    </div>
  );
}
